import { min } from 'lib/array/arrayUtils'

export const isSoloTraveller = (capacities: Array<App.RoomCapacity>) => {
  if (!capacities || capacities.length === 0) return false
  return capacities.every(cap => cap.adults === 1 && cap.children === 0 && cap.infants === 0 && cap.teenagers === 0)
}
export function getLowestPricePackage(
  offer: App.HotelOffer | App.TourOffer | App.BundledOffer | App.HotelOfferSummary | App.TourOfferSummary | App.VillaOffer | App.BundleOffer,
  offerPackages: Array<App.HotelPackage>,
) {
  const defaultOptions = new Set(offer.defaultOptions)
  let packages = offerPackages.filter(x => defaultOptions.has(x.uniqueKey))
  packages = packages.length > 0 ? packages : offerPackages

  // Temporary fix to exclude solo traveller packages
  const filteredSoloPackages = packages.filter(pkg => !isSoloTraveller(pkg.roomRate?.capacities ?? []))
  packages = filteredSoloPackages.length > 0 ? filteredSoloPackages : packages

  return min(packages, pkg => (pkg.memberPrice || pkg.price) + (pkg.surcharge ?? 0) || Number.MAX_SAFE_INTEGER)
}

/**
 * Lead package for a tile that advertises an offer's minimum length of stay.
 *
 * Delegates to `getLowestPricePackage` rather than taking a raw cheapest-by-price pass, so a
 * min-stay tile and the offer page agree on which package sets the price. A raw pass leads with
 * a solo-traveller room rate whenever one is cheapest; the offer page sets those aside while a
 * bookable alternative exists, so the tile would advertise a price the offer page won't honour.
 *
 * Delegating inherits the offer page's whole lead-package rule, not only the solo exclusion —
 * `defaultOptions` narrowing and ranking by `(memberPrice || price) + surcharge` come with it.
 * That is deliberate. The requirement is that the tile and the offer page show the *same* lead
 * price for the same nights, so they have to run the same selection; a tile-only variant would
 * be free to drift again the moment either rule changes. The trade-off is that the tile is a
 * mirror of the offer page's lead price rather than an independent cheapest-bookable scan.
 */
export function getLowestPriceMinDurationPackage(
  offer: App.HotelOffer | App.HotelOfferSummary,
  offerPackages: Array<App.HotelPackage>,
  minDuration: number,
): App.HotelPackage | undefined {
  const minDurationPackages = offerPackages.filter(pkg => pkg.duration === minDuration && pkg.price > 0)
  if (minDurationPackages.length === 0) return undefined

  return getLowestPricePackage(offer, minDurationPackages)
}
